import axios from 'axios';
import Logo from 'jsx:../../img/tandem-logo-horizontal.svg';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Alert from '../../components/Alert';
import { useUserStore } from '../../stores/userStore';

const TOU_URL = 'https://nowintandem.com/terms-of-use/';
const PRIVACY_URL = 'https://nowintandem.com/privacy/';

const UpdateReferralStatus = () => {
	const tokenLogin = useUserStore((state) => state.tokenLogin);
	const verifyUser = useUserStore((state) => state.verifyUser);
	// Session id returned by token-login, used to scope subsequent calls.
	const userId = useUserStore((state) => state.session?.userId);

	const [notAccepted, setNotAccepted] = useState(true);
	const [referrals, setReferrals] = useState([]);
	const [assessments, setAssessments] = useState([]);
	const [statuses, setStatuses] = useState({});
	const [needsMet, setNeedsMet] = useState({});
	const [saved, setSaved] = useState({});
	const [savingId, setSavingId] = useState(null);
	const [loading, setLoading] = useState(false);
	const [loadError, setLoadError] = useState(false);
	// Token-login blocks the UI until it resolves; a failure disables Continue.
	const [authPending, setAuthPending] = useState(true);
	const [authError, setAuthError] = useState(false);

	const paramsString = window.location.hash.split('?')[1] || '';
	const token = new URLSearchParams(paramsString).get('t');

	// Establish the session through userStore. token-login isn't part of the
	// store's normal flows, so it has its own action; cookie/header setup lives
	// there alongside signIn.
	useEffect(() => {
		let cancelled = false;

		const authenticate = async () => {
			if (!token) {
				// No token in the URL. On a refresh, an existing session cookie may
				// survive — verifyUser restores the auth header and loads the user.
				// If that leaves us signed in, skip the accept step.
				await verifyUser();
				if (cancelled) return;
				if (useUserStore.getState().isSignedIn()) setNotAccepted(false);
				setAuthPending(false);
				return;
			}

			setAuthPending(true);
			setAuthError(false);
			try {
				await tokenLogin(token);
			} catch {
				if (!cancelled) setAuthError(true);
			} finally {
				if (!cancelled) setAuthPending(false);
			}
		};

		authenticate();

		return () => {
			cancelled = true;
		};
	}, [token, tokenLogin, verifyUser]);

	useEffect(() => {
		if (notAccepted) return;

		let cancelled = false;
		setLoading(true);
		setLoadError(false);
		Promise.all([
			axios.get(`/clients/${userId}/followup-requests`).then((res) => {
				if (!cancelled) setReferrals(res.data);
			}),
			axios.get(`/clients/${userId}/needed-assessments`).then((res) => {
				if (!cancelled) setAssessments(res.data);
			}),
		])
			.catch(() => {
				if (!cancelled) setLoadError(true);
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});

		return () => {
			cancelled = true;
		};
	}, [notAccepted, userId]);

	// Group referrals by coordinating agency, sort groups by agency_name,
	// and sort each group's referrals by send_followup_on (soonest first).
	const groups = Object.values(
		referrals.reduce((acc, referral) => {
			const agency = referral.coord_agency;
			if (!acc[agency.id]) {
				acc[agency.id] = { agency, referrals: [] };
			}
			acc[agency.id].referrals.push(referral);
			return acc;
		}, {}),
	)
		.sort((a, b) => a.agency.agency_name.localeCompare(b.agency.agency_name))
		.map((group) => ({
			...group,
			referrals: group.referrals
				.slice()
				.sort(
					(a, b) => new Date(a.send_followup_on) - new Date(b.send_followup_on),
				),
		}));

	// Group assessments by coordinating agency, then by service within each
	// agency. Agencies and services are sorted alphabetically by name.
	const assessmentGroups = Object.values(
		assessments.reduce((acc, assessment) => {
			const agencyName = assessment.coord_agency_name;
			if (!acc[agencyName]) {
				acc[agencyName] = { agencyName, services: {} };
			}
			const serviceName = assessment.service_name;
			if (!acc[agencyName].services[serviceName]) {
				acc[agencyName].services[serviceName] = {
					serviceName,
					assessments: [],
				};
			}
			acc[agencyName].services[serviceName].assessments.push(assessment);
			return acc;
		}, {}),
	)
		.sort((a, b) => a.agencyName.localeCompare(b.agencyName))
		.map((group) => ({
			...group,
			services: Object.values(group.services).sort((a, b) =>
				a.serviceName.localeCompare(b.serviceName),
			),
		}));

	const saveStatus = (referralId) => {
		const status = statuses[referralId];
		if (!status) return;

		const payload = {
			serviceProvided: status,
		};
		// needsMet only applies when the service was provided, and is optional.
		if (status === 'provided' && needsMet[referralId] !== undefined) {
			payload.needsMet = needsMet[referralId];
		}

		setSavingId(referralId);
		axios
			.post(`/clients/${userId}/followup-response/${referralId}`, payload)
			.then(() => {
				setSaved((prev) => ({ ...prev, [referralId]: true }));
				// Let the fade-out play, then drop the referral from the list.
				setTimeout(() => {
					setReferrals((prev) => prev.filter((r) => r.id !== referralId));
				}, 700);
			})
			.finally(() => {
				setSavingId(null);
			});
	};

	// Block the UI until token-login resolves.
	if (authPending) {
		return (
			<div className="flex justify-center px-4 py-10">
				<span className="loading loading-dots loading-lg text-primary"></span>
			</div>
		);
	}

	if (notAccepted) {
		return (
			<div className="flex flex-col items-center max-w-md gap-6 px-4 py-10 mx-auto text-center">
				<Logo className="w-auto h-12" />
				{!token && (
					<Alert message="Something went wrong. Please use the link provided to you to access this page." />
				)}
				{authError && (
					<Alert message="We couldn’t verify your link. Please use the link provided to you to access this page." />
				)}
				<p className="mb-2 text-sm">
					By clicking “Continue,” you consent to sharing your personally
					identifiable information (PII) and, if applicable, protected health
					information (PHI), in accordance with our Privacy Policy.
				</p>
				<p className="mb-2 text-sm">
					By clicking “Continue,” you accept the terms of our{' '}
					<a
						className="link link-primary"
						href={TOU_URL}
						target="_blank"
						rel="noreferrer"
					>
						Terms of Use
					</a>{' '}
					and{' '}
					<a
						className="link link-primary"
						href={PRIVACY_URL}
						target="_blank"
						rel="noreferrer"
					>
						Privacy Policy
					</a>
					.
				</p>
				<button
					type="button"
					className="text-white btn btn-primary btn-sm btn-block"
					onClick={() => setNotAccepted(false)}
					disabled={!token || authError}
				>
					Continue
				</button>
			</div>
		);
	}

	return (
		<div className="max-w-3xl px-4 py-6 mx-auto">
			<h1 className="mb-4 text-2xl font-bold">Items needing your attention</h1>
			<h3 className="mb-2 text-lg font-bold">Was Service Provided?</h3>
			{loading && <p className="mb-2">Loading referrals…</p>}
			{loadError && (
				<Alert message="Something went wrong loading your referrals. Please try again later." />
			)}
			{!loading && !loadError && groups.length === 0 && (
				<p className="mb-2">There are no referrals that need your attention.</p>
			)}
			{groups.map(({ agency, referrals: agencyReferrals }) => (
				<div key={agency.id} className="mb-8">
					<h2 className="mb-2 text-lg font-semibold">{agency.agency_name}</h2>
					<ul className="flex flex-col gap-3">
						{agencyReferrals.map((referral) => (
							<li
								key={referral.id}
								className={`p-4 border rounded-lg border-base-300 transition-opacity duration-700 ease-out ${
									saved[referral.id] ? 'opacity-0' : 'opacity-100'
								}`}
							>
								<div className="font-medium">{referral.taxonomy_detail}</div>
								<div className="text-sm opacity-70">
									Follow-up by{' '}
									{new Date(referral.send_followup_on).toLocaleDateString()}
								</div>
								<div className="flex flex-col gap-3 mt-3 sm:flex-row sm:items-end">
									<label className="flex flex-col gap-1 grow">
										<span className="text-sm">Was service provided?</span>
										<select
											aria-label="Was service provided?"
											className="w-full select select-bordered select-sm"
											value={statuses[referral.id] ?? ''}
											disabled={saved[referral.id]}
											onChange={({ target }) => {
												setStatuses((prev) => ({
													...prev,
													[referral.id]: target.value,
												}));
												// needsMet only applies to "provided"; clear it otherwise.
												if (target.value !== 'provided') {
													setNeedsMet((prev) => ({
														...prev,
														[referral.id]: undefined,
													}));
												}
											}}
										>
											<option value="" disabled></option>
											<option value="provided">Service was provided</option>
											<option value="not_provided">
												Service was not provided
											</option>
											<option value="waiting">
												Waiting for service to be provided
											</option>
										</select>
									</label>
									<label className="flex flex-col gap-1 sm:w-40">
										<span className="text-sm">
											Were needs met?
											{statuses[referral.id] === 'provided' && (
												<span className="ml-0.5 text-error">*</span>
											)}
										</span>
										<select
											aria-label="Were needs met?"
											className="w-full select select-bordered select-sm"
											value={
												needsMet[referral.id] === undefined
													? ''
													: String(needsMet[referral.id])
											}
											disabled={
												statuses[referral.id] !== 'provided' ||
												saved[referral.id]
											}
											onChange={({ target }) =>
												setNeedsMet((prev) => ({
													...prev,
													[referral.id]:
														target.value === ''
															? undefined
															: target.value === 'true',
												}))
											}
										>
											<option value=""></option>
											<option value="true">Yes</option>
											<option value="false">No</option>
										</select>
									</label>
									<button
										type="button"
										className="text-white btn btn-primary btn-sm sm:w-auto"
										disabled={
											!statuses[referral.id] ||
											// "Were needs met?" is required once service is marked provided.
											(statuses[referral.id] === 'provided' &&
												needsMet[referral.id] === undefined) ||
											savingId === referral.id ||
											saved[referral.id]
										}
										onClick={() => saveStatus(referral.id)}
									>
										{saved[referral.id] ? 'Saved' : 'Save'}
									</button>
								</div>
							</li>
						))}
					</ul>
				</div>
			))}
			{assessments.length > 0 && (
				<>
					<h3 className="mt-4 mb-2 text-lg font-bold">
						Assessments to Complete
					</h3>
					{!loading && !loadError && assessments.length === 0 && (
						<p className="mb-2">
							There are no assessments that need your attention.
						</p>
					)}
					{!loading && !loadError && assessments.length > 0 && (
						<div className="mb-8">
							{assessmentGroups.map((group) => (
								<div key={group.agencyName} className="mb-6">
									<h4 className="mb-2 text-lg font-semibold">
										{group.agencyName}
									</h4>
									{group.services.map((service) => (
										<div key={service.serviceName} className="mb-4">
											<h5 className="mb-2 text-base font-medium">
												{service.serviceName}
											</h5>
											{service.assessments.map((assessment) => (
												<div
													key={assessment.care_quest_group_id}
													className="mb-2"
												>
													<Link
														className="btn btn-primary btn-block"
														to={`/assessments/${assessment.care_quest_group_id}`}
														state={{ from: '/cca' }}
													>
														{assessment.name}
													</Link>
												</div>
											))}
										</div>
									))}
								</div>
							))}
						</div>
					)}
				</>
			)}
		</div>
	);
};

export default UpdateReferralStatus;
